import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

export default function GoldCarousel() {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/home-carousel`).then(r => setProducts(r.data.products || []));
  }, []);

  useEffect(() => {
    if (products.length < 2 || paused) return;
    timerRef.current = setInterval(() => setActive(i => (i + 1) % products.length), 5500);
    return () => clearInterval(timerRef.current);
  }, [products.length, paused]);

  if (!products.length) return null;

  const go = (dir) => setActive(i => (i + dir + products.length) % products.length);
  const current = products[active];

  return (
    <section
      className="relative overflow-hidden bg-[#0f0a08] text-white"
      data-testid="gold-cinematic-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Full-bleed image stack with slow ken-burns */}
      <div className="relative h-[70vh] min-h-[520px] max-h-[780px]">
        {products.map((p, i) => (
          <div
            key={p.product_id}
            className={`absolute inset-0 transition-opacity duration-[1400ms] ease-in-out ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
            data-testid={`gold-slide-${i}`}
          >
            <div className="absolute inset-0 overflow-hidden">
              {/* First slide is above-the-fold → eager+high priority. Later slides
                  wait until the carousel actually rotates to them. decoding=async
                  keeps the main thread free during scroll. */}
              <img
                src={p.images?.[0]}
                alt={p.name}
                loading={i === 0 ? "eager" : "lazy"}
                fetchpriority={i === 0 ? "high" : "low"}
                decoding="async"
                className={`w-full h-full object-cover ${i === active ? "kenburns" : ""}`}
              />
            </div>
            {/* Cinematic gradients */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40" />
          </div>
        ))}

        {/* Copy layer */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div key={`copy-${active}`} className="max-w-xl fade-in-slow">
            <p className="text-[11px] tracking-[0.35em] uppercase text-[#e8c66d] mb-4">
              The Cinematic Gold Edit
            </p>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-white drop-shadow-lg"
              data-testid="carousel-title"
            >
              {current.name}
            </h2>
            <p className="mt-5 text-[#e8dfd0] text-sm sm:text-base leading-relaxed line-clamp-3 max-w-md">
              {current.description}
            </p>
            <div className="mt-6 flex items-baseline gap-3">
              <span className="text-2xl sm:text-3xl font-semibold text-white">
                {inr(current.price)}
              </span>
              <span className="text-xs uppercase tracking-widest text-[#c9b785]">
                {current.purity}{current.hide_weight ? "" : ` · ${current.weight}g`}
              </span>
            </div>
            <div className="mt-8">
              <Link to={`/product/${current.product_id}`}>
                <button
                  className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-gradient-to-r from-[#d4af37] via-[#f4e5b5] to-[#c5a059] text-[#2C2A29] font-medium hover:shadow-2xl hover:shadow-[#d4af37]/40 transition-all"
                  data-testid="carousel-cta"
                >
                  Explore this piece
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Nav arrows */}
        {products.length > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 flex items-center justify-center transition"
              data-testid="carousel-prev"
              aria-label="Previous"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => go(1)}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 flex items-center justify-center transition"
              data-testid="carousel-next"
              aria-label="Next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        {/* Bottom thumbnail rail */}
        {products.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-3">
            {products.map((p, i) => (
              <button
                key={p.product_id}
                onClick={() => setActive(i)}
                className={`h-14 w-14 sm:h-16 sm:w-16 rounded-lg overflow-hidden border-2 transition-all ${
                  i === active
                    ? "border-[#d4af37] scale-110 shadow-lg shadow-black/60"
                    : "border-white/20 opacity-60 hover:opacity-100"
                }`}
                data-testid={`carousel-thumb-${i}`}
                aria-label={p.name}
              >
                <img src={p.images?.[0]} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
